import React, { Component } from 'react';
import { NavLink } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { LayoutDashboardIcon, FileBarChart2Icon, WrenchIcon, BookmarkIcon, CalendarIcon, FlaskConicalIcon, UsersIcon, MapPinIcon, SettingsIcon, ShieldCheckIcon, XIcon } from 'lucide-react';
import { useApp, Theme } from '../../context/AppContext';
interface NavItem {
  label: string;
  to: string;
  icon: ComponentType<{
    className?: string;
  }>;
  disabled?: boolean;
}
const reportingNav: NavItem[] = [{
  label: 'Overview',
  to: '/',
  icon: LayoutDashboardIcon
}, {
  label: 'Report Library',
  to: '/reports',
  icon: FileBarChart2Icon
}, {
  label: 'Report Builder',
  to: '/builder',
  icon: WrenchIcon
}, {
  label: 'My Templates',
  to: '/templates',
  icon: BookmarkIcon
}];
const platformNav: NavItem[] = [{
  label: 'Appointments',
  to: '/appointments',
  icon: CalendarIcon,
  disabled: true
}, {
  label: 'Results',
  to: '/results',
  icon: FlaskConicalIcon,
  disabled: true
}, {
  label: 'Clearances',
  to: '/clearances',
  icon: ShieldCheckIcon,
  disabled: true
}, {
  label: 'Collection Sites',
  to: '/sites',
  icon: MapPinIcon,
  disabled: true
}, {
  label: 'Users',
  to: '/users',
  icon: UsersIcon,
  disabled: true
}];
const themeStyles = {
  dark: {
    groupTitle: 'text-navy-400',
    disabled: 'text-navy-500',
    linkActive: 'bg-white/10 text-white',
    linkIdle: 'text-navy-300 hover:bg-white/5 hover:text-white',
    logoTitle: 'text-white',
    logoSub: 'text-navy-300',
    settings: 'text-navy-500'
  },
  light: {
    groupTitle: 'text-navy-400',
    disabled: 'text-navy-300',
    linkActive: 'bg-navy-50 text-navy-900',
    linkIdle: 'text-navy-500 hover:bg-navy-50/60 hover:text-navy-900',
    logoTitle: 'text-navy-900',
    logoSub: 'text-navy-400',
    settings: 'text-navy-300'
  }
};
function NavGroup({
  title,
  items,
  theme,
  onNavigate
}: {
  title: string;
  items: NavItem[];
  theme: Theme;
  onNavigate?: () => void;
}) {
  const s = themeStyles[theme];
  return <div data-id="element-95">
      <p className={`px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.18em] ${s.groupTitle}`} data-id="element-96">
        {title}
      </p>
      <ul className="space-y-1" data-id="element-97">
        {items.map(item => item.disabled ? <li key={item.label} data-id="element-98">
              <span className={`flex cursor-not-allowed items-center gap-3 rounded-lg px-3 py-2 text-sm ${s.disabled}`} title="Coming soon in the new UI" data-id="element-99">
                <item.icon className="h-4 w-4 shrink-0" data-id="element-100" />
                {item.label}
              </span>
            </li> : <li key={item.label} data-id="element-101">
              <NavLink to={item.to} end={item.to === '/'} onClick={onNavigate} className={({
          isActive
        }) => `relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${isActive ? s.linkActive : s.linkIdle}`} data-id="element-102">
                {({
            isActive
          }) => <>
                    {isActive && <span className={`absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-full ${theme === 'dark' ? 'bg-gold-400' : 'bg-gold-500'}`} data-id="element-103" />}
                    <item.icon className={`h-4 w-4 shrink-0 ${isActive ? theme === 'dark' ? 'text-gold-300' : 'text-gold-600' : ''}`} data-id="element-104" />
                    {item.label}
                  </>}
              </NavLink>
            </li>)}
      </ul>
    </div>;
}
function SidebarContent({
  onNavigate
}: {
  onNavigate?: () => void;
}) {
  const {
    theme
  } = useApp();
  const s = themeStyles[theme];
  return <>
      <div className="flex h-16 items-center gap-3 px-5" data-id="element-105">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gold-500" data-id="element-106">
          <ShieldCheckIcon className="h-4.5 w-4.5 text-navy-950" data-id="element-107" />
        </div>
        <div className="leading-tight" data-id="element-108">
          <p className={`text-sm font-semibold tracking-[0.12em] ${s.logoTitle}`} data-id="element-109">
            SURELINK
          </p>
          <p className={`text-[9px] font-medium uppercase tracking-[0.28em] ${s.logoSub}`} data-id="element-110">
            Reporting
          </p>
        </div>
      </div>
      <nav className="flex-1 space-y-7 overflow-y-auto px-3 py-6 thin-scroll" aria-label="Main navigation" data-id="element-111">
        <NavGroup title="Reporting" items={reportingNav} theme={theme} onNavigate={onNavigate} data-id="element-112" />
        <NavGroup title="Platform" items={platformNav} theme={theme} onNavigate={onNavigate} data-id="element-113" />
      </nav>
      <div className="px-3 py-4" data-id="element-114">
        <span className={`flex cursor-not-allowed items-center gap-3 rounded-lg px-3 py-2 text-sm ${s.settings}`} data-id="element-115">
          <SettingsIcon className="h-4 w-4" data-id="element-116" />
          Settings
        </span>
      </div>
    </>;
}
export function Sidebar() {
  const {
    theme
  } = useApp();
  return <aside className={`hidden w-60 shrink-0 flex-col rounded-r-2xl lg:flex ${theme === 'dark' ? 'bg-navy-950' : 'card-float bg-white'}`} data-id="element-117">
      <SidebarContent data-id="element-118" />
    </aside>;
}
export function MobileSidebar() {
  const {
    theme,
    mobileNavOpen,
    setMobileNavOpen
  } = useApp();
  return <AnimatePresence data-id="element-119">
      {mobileNavOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} className="fixed inset-0 z-40 bg-navy-950/30 backdrop-blur-[2px] lg:hidden" onClick={() => setMobileNavOpen(false)} aria-hidden="true" data-id="element-120" />
          <motion.aside initial={{
        x: '-100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '-100%'
      }} transition={{
        type: 'spring',
        stiffness: 380,
        damping: 36
      }} className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col rounded-r-2xl lg:hidden ${theme === 'dark' ? 'bg-navy-950' : 'card-float-lg bg-white'}`} role="dialog" aria-modal="true" aria-label="Navigation menu" data-id="element-121">
            <button type="button" onClick={() => setMobileNavOpen(false)} aria-label="Close navigation menu" className={`absolute right-3 top-4 flex h-8 w-8 items-center justify-center rounded-full transition-colors ${theme === 'dark' ? 'text-navy-300 hover:bg-white/10 hover:text-white' : 'text-navy-400 hover:bg-navy-50 hover:text-navy-900'}`} data-id="element-122">
              <XIcon className="h-4 w-4" data-id="element-123" />
            </button>
            <SidebarContent onNavigate={() => setMobileNavOpen(false)} data-id="element-124" />
          </motion.aside>
        </>}
    </AnimatePresence>;
}